// Hyperspace jump: the interstellar transition between star systems.
//
// Three layers ride with the camera (the group copies its pose every
// frame, so everything here lives in camera space, -Z ahead):
//
//   1. Streak field: ~12k stars as screen-space line quads, animated
//      entirely in the vertex shader from one travel scalar. At rest
//      they are points; as speed builds they stretch into streaks that
//      pour past the camera, the near ones wide and fast, the far ones
//      converging on the vanishing point.
//   2. Tunnel: a sphere around the camera whose shader maps each view
//      ray onto an infinite cylinder (depth = cot of the angle off the
//      axis), so the walls read as a corridor of ionised filaments
//      rushing by. An aperture closes it in from the edges on entry
//      and opens it from the centre on exit, which is how the new
//      system is revealed at the end of the tunnel.
//   3. Flash + core glow in the same shader (premultiplied blending),
//      tinted by the departure and destination stars, never white.
//
// The caller runs the timeline with start({ cruiseSeconds, ... }) and
// reads back cameraFx() each frame for the FOV punch and shake it
// applies around the render. The swap to the destination system is
// fired through onSwap at the middle of the cruise, when the tunnel
// fully covers the view.

import * as THREE from 'three';

const STREAKS_HIGH = 12000;
const STREAKS_LOW = 5000;
const CORRIDOR = 140;        // streak corridor depth, in group units
const FIELD_RADIUS = 11;     // outer radius of the streak cylinder
const TUNNEL_RADIUS = 4;     // backdrop sphere radius, in group units

const SPOOL = 2.4;           // seconds: stars stretch, drive charges
const PUNCH = 0.55;          // seconds: tunnel wraps in, flash
const EXIT = 2.1;            // seconds: tunnel opens, streaks collapse
const FLASH_BLUE = new THREE.Color(0.25, 0.55, 1.4);

const smooth = (a, b, x) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
const lerp = (a, b, t) => a + (b - a) * t;
const easeInCubic = (t) => t * t * t;
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// Planck-ish blackbody tint, normalised so the brightest channel is 1.
function kelvinToRgb(k) {
  const t = k / 100;
  let r, g, b;
  if (t <= 66) {
    r = 255;
    g = 99.47 * Math.log(t) - 161.12;
    b = t <= 19 ? 0 : 138.52 * Math.log(t - 10) - 305.04;
  } else {
    r = 329.7 * Math.pow(t - 60, -0.1332);
    g = 288.12 * Math.pow(t - 60, -0.0755);
    b = 255;
  }
  const c = [r, g, b].map((v) => Math.min(255, Math.max(0, v)) / 255);
  const m = Math.max(...c) || 1;
  return c.map((v) => v / m);
}

const STREAK_VERT = /* glsl */`
  attribute vec4 aStar;   // angle, radius, phase, rand
  attribute vec3 aColor;
  uniform float uTravel;
  uniform float uLen;
  uniform float uCorridor;
  uniform float uWidth;
  uniform float uNear;
  uniform vec2 uResolution;
  varying vec2 vQuad;
  varying vec3 vColor;
  varying float vFade;

  void main() {
    float speedVar = 0.82 + 0.36 * aStar.w;
    float span = uCorridor + 6.0;
    float zHead = -uCorridor + mod(aStar.z * span + uTravel * speedVar, span);
    // Tails stop short of the vanishing point: a streak's tail reaches
    // at most ~2.4x its head depth, otherwise every tail in the field
    // converges on the centre and burns it white.
    float len = min(uLen * (0.55 + 0.9 * aStar.w), max(-zHead, 0.5) * 1.4);
    vec2 xy = vec2(cos(aStar.x), sin(aStar.x)) * aStar.y;
    vec3 head = vec3(xy, min(zHead, -uNear));
    vec3 tail = vec3(xy, min(zHead - len, -uNear * 1.001));

    vec4 ch = projectionMatrix * modelViewMatrix * vec4(head, 1.0);
    vec4 ct = projectionMatrix * modelViewMatrix * vec4(tail, 1.0);
    vec2 sh = ch.xy / ch.w;
    vec2 st = ct.xy / ct.w;
    vec2 d = (sh - st) * uResolution;
    float dl = length(d);
    vec2 dir = dl > 1e-4 ? d / dl : vec2(1.0, 0.0);
    vec2 nrm = vec2(-dir.y, dir.x);

    // Nearer stars are fatter; far ones shrink to a hairline.
    float px = uWidth * clamp(9.0 / max(-head.z, 0.4), 0.55, 4.0) * (0.7 + 0.6 * aStar.w);
    vec4 p = mix(ct, ch, position.x);
    vec2 off = nrm * position.y * px + dir * (position.x * 2.0 - 1.0) * px;
    p.xy += off / uResolution * 2.0 * p.w;
    gl_Position = p;

    vQuad = vec2(position.x, position.y);
    vColor = aColor;
    // Emerge out of the dark far end, dim right at the camera plane.
    float depth = -zHead;
    // Point sources dim with distance, so the vanishing point stays a
    // glow rather than a pile-up of every streak in the corridor.
    vFade = smoothstep(uCorridor, uCorridor * 0.55, depth) * smoothstep(-2.0, 1.5, depth + 1.0)
      * pow(clamp(10.0 / max(depth, 1.0), 0.0, 1.0), 1.4);
  }
`;

const STREAK_FRAG = /* glsl */`
  uniform float uBright;
  uniform float uOpacity;
  uniform vec3 uTint;
  uniform float uTintMix;
  varying vec2 vQuad;
  varying vec3 vColor;
  varying float vFade;

  void main() {
    float across = exp(-vQuad.y * vQuad.y * 3.2);
    float along = pow(clamp(vQuad.x, 0.0, 1.0), 1.7);
    float head = exp(-pow((1.0 - vQuad.x) * 7.0, 2.0));
    vec3 col = mix(vColor, vColor * uTint, uTintMix);
    float e = across * (along + head * 0.9) * vFade * uOpacity;
    gl_FragColor = vec4(col * e * uBright, 1.0);
  }
`;

const TUNNEL_VERT = /* glsl */`
  varying vec3 vDir;
  void main() {
    vDir = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const TUNNEL_FRAG = /* glsl */`
  uniform float uTime;
  uniform float uTravel;
  uniform float uAperture;
  uniform float uCover;
  uniform float uFlash;
  uniform float uCore;
  uniform float uRim;
  uniform float uSpeed;
  uniform float uTintMix;
  uniform vec3 uTintFrom;
  uniform vec3 uTintTo;
  uniform vec3 uFlashColor;
  varying vec3 vDir;

  float hash(vec3 p) {
    p = fract(p * 0.3183099 + 0.1);
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }
  float vnoise(vec3 x) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    return mix(mix(mix(hash(i), hash(i + vec3(1, 0, 0)), f.x),
                   mix(hash(i + vec3(0, 1, 0)), hash(i + vec3(1, 1, 0)), f.x), f.y),
               mix(mix(hash(i + vec3(0, 0, 1)), hash(i + vec3(1, 0, 1)), f.x),
                   mix(hash(i + vec3(0, 1, 1)), hash(i + vec3(1, 1, 1)), f.x), f.y), f.z);
  }
  float fbm(vec3 p) {
    float s = 0.0, a = 0.5;
    for (int o = 0; o < 4; o++) { s += a * vnoise(p); p = p * 2.03 + 11.7; a *= 0.5; }
    return s;
  }

  void main() {
    vec3 d = normalize(vDir);
    float cz = -d.z;
    float theta = acos(clamp(cz, -1.0, 1.0));
    float sn = max(sin(theta), 1e-3);
    float depth = clamp(cz / sn, -3.0, 80.0);
    float phi = atan(d.y, d.x);
    vec2 ring = vec2(cos(phi), sin(phi));

    // Tunnel wall coordinate: v runs down the corridor, scrolled by travel.
    float v = depth * 9.0 + uTravel * 0.6;
    float fw = fwidth(v);
    float detail = 1.0 - smoothstep(4.0, 30.0, fw);

    vec3 tint = mix(uTintFrom, uTintTo, uTintMix);

    // Broad nebular masses, then ridged filaments, then fine threads.
    // The corridor is mostly dark: filaments are thin ridges gated by
    // the nebula field, so the light comes in sheets and gaps rather
    // than as a uniform wall.
    float neb = fbm(vec3(ring * 1.4, v * 0.004 + uTime * 0.05));
    float gate = smoothstep(0.42, 0.72, neb);
    float fil = 1.0 - abs(fbm(vec3(ring * 3.2, v * 0.011 - uTime * 0.2)) * 2.0 - 1.0);
    fil = pow(fil, 22.0) * (0.25 + 0.75 * gate);
    float thr = 1.0 - abs(vnoise(vec3(ring * 13.0, v * 0.035)) * 2.0 - 1.0);
    thr = pow(thr, 40.0) * detail;

    vec3 deep = vec3(0.002, 0.004, 0.014);
    vec3 nebCol = mix(vec3(0.05, 0.02, 0.14), vec3(0.01, 0.06, 0.16), neb);
    nebCol = mix(nebCol, tint * 0.08, 0.3);
    vec3 filCol = mix(vec3(0.18, 0.55, 1.2), tint * 0.9, 0.25);
    vec3 col = deep;
    col += nebCol * gate * 1.6;
    col += filCol * fil * (0.35 + 0.55 * uSpeed);
    col += vec3(0.45, 0.65, 1.2) * thr * (0.25 + 0.5 * uSpeed) * gate;

    // Passing rings of light, a few per second at full speed.
    float rp = fract(v * 0.0045);
    col += filCol * 0.5 * exp(-pow((rp - 0.5) * 70.0, 2.0)) * gate * detail;

    // Far down the corridor everything converges on the core glow.
    float fog = smoothstep(6.0, 60.0, depth);
    vec3 coreCol = mix(vec3(0.3, 0.5, 1.1), tint, 0.4);
    col = mix(col, coreCol * 0.06, fog);
    float coreGlow = exp(-theta * theta * 160.0) * 0.9 + exp(-theta * theta * 14.0) * 0.08;
    col += coreCol * coreGlow * uCore;

    // Behind the camera the corridor falls dark.
    col *= smoothstep(-2.5, 0.2, depth);

    // Aperture with an organic edge and an energy rim on it.
    float wob = (vnoise(vec3(ring * 3.0, uTime * 0.9)) - 0.5) * 0.22;
    float edge = uAperture + wob;
    float mask = smoothstep(edge, edge + 0.22, theta);
    float rim = exp(-pow((theta - edge - 0.08) / 0.06, 2.0)) * uRim;
    float a = uCover * mask;

    vec3 rgb = col * a + (filCol + tint * 0.4) * rim * uCover;
    // Flash: a bloom-hot burst at the vanishing point, star tinted.
    float fl = exp(-theta * theta * 40.0) * 2.2 + exp(-theta * theta * 4.0) * 0.25;
    rgb += uFlashColor * fl * uFlash;
    gl_FragColor = vec4(rgb, a);
  }
`;

export function createHyperspace({ quality = 'high' } = {}) {
  const count = quality === 'low' ? STREAKS_LOW : STREAKS_HIGH;
  const group = new THREE.Group();
  group.name = 'hyperspace';
  group.visible = false;
  group.frustumCulled = false;

  // ---- streaks ------------------------------------------------------------
  const quad = new THREE.InstancedBufferGeometry();
  quad.setAttribute('position', new THREE.Float32BufferAttribute([
    0, -1, 0, 1, -1, 0, 1, 1, 0,
    0, -1, 0, 1, 1, 0, 0, 1, 0
  ], 3));
  const star = new Float32Array(count * 4);
  const color = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    // Denser toward the axis so the vanishing point is rich, with a
    // sprinkle of near flecks that whip past the frame edge.
    const u = Math.random();
    const r = u < 0.08 ? 0.35 + Math.random() * 1.2 : 0.8 + Math.pow(Math.random(), 0.75) * FIELD_RADIUS;
    star[i * 4] = Math.random() * Math.PI * 2;
    star[i * 4 + 1] = r;
    star[i * 4 + 2] = Math.random();
    star[i * 4 + 3] = Math.random();
    const roll = Math.random();
    const kelvin = roll < 0.12 ? 3200 + Math.random() * 1800
      : roll < 0.3 ? 5200 + Math.random() * 2000
        : 8000 + Math.random() * 22000;
    // Power-law magnitudes: most stars faint, a few hot enough to bloom.
    const mag = 0.08 + 0.92 * Math.pow(Math.random(), 5);
    const c = kelvinToRgb(kelvin);
    color[i * 3] = c[0] * mag; color[i * 3 + 1] = c[1] * mag; color[i * 3 + 2] = c[2] * mag;
  }
  quad.setAttribute('aStar', new THREE.InstancedBufferAttribute(star, 4));
  quad.setAttribute('aColor', new THREE.InstancedBufferAttribute(color, 3));
  quad.instanceCount = count;

  const streakUniforms = {
    uTravel: { value: 0 },
    uLen: { value: 0 },
    uCorridor: { value: CORRIDOR },
    uWidth: { value: 1.0 },
    uNear: { value: 0.05 },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uBright: { value: 2.2 },
    uOpacity: { value: 0 },
    uTint: { value: new THREE.Color(0.7, 0.85, 1.25) },
    uTintMix: { value: 0 }
  };
  const streakMat = new THREE.ShaderMaterial({
    uniforms: streakUniforms,
    vertexShader: STREAK_VERT,
    fragmentShader: STREAK_FRAG,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false
  });
  const streaks = new THREE.Mesh(quad, streakMat);
  streaks.frustumCulled = false;
  streaks.renderOrder = 10001;
  group.add(streaks);

  // ---- tunnel -------------------------------------------------------------
  const tunnelUniforms = {
    uTime: { value: 0 },
    uTravel: { value: 0 },
    uAperture: { value: 2.2 },
    uCover: { value: 0 },
    uFlash: { value: 0 },
    uCore: { value: 0 },
    uRim: { value: 0 },
    uSpeed: { value: 0 },
    uTintMix: { value: 0 },
    uTintFrom: { value: new THREE.Color(0.55, 0.7, 1.0) },
    uTintTo: { value: new THREE.Color(0.55, 0.7, 1.0) },
    uFlashColor: { value: new THREE.Color(0.6, 0.8, 1.3) }
  };
  const tunnelMat = new THREE.ShaderMaterial({
    uniforms: tunnelUniforms,
    vertexShader: TUNNEL_VERT,
    fragmentShader: TUNNEL_FRAG,
    side: THREE.BackSide,
    transparent: true,
    depthWrite: false,
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.OneFactor,
    blendDstAlpha: THREE.OneMinusSrcAlphaFactor,
    toneMapped: false
  });
  const tunnel = new THREE.Mesh(new THREE.SphereGeometry(TUNNEL_RADIUS, 96, 64), tunnelMat);
  tunnel.frustumCulled = false;
  tunnel.renderOrder = 10000;
  group.add(tunnel);

  // ---- HUD ---------------------------------------------------------------
  let hud = null;
  function ensureHud() {
    if (hud) return hud;
    const el = document.createElement('div');
    el.id = 'hyperspace-hud';
    el.innerHTML = `
      <div class="hs-label">Hyperspace</div>
      <div class="hs-dest"></div>
      <div class="hs-bar"><div class="hs-fill"></div></div>
      <div class="hs-eta"></div>`;
    const style = document.createElement('style');
    style.textContent = `
      #hyperspace-hud { position: fixed; left: 50%; top: 172px; transform: translateX(-50%);
        z-index: 420; pointer-events: none; min-width: 280px; text-align: center; padding: 14px 22px 14px;
        background: #0a0d13; border-radius: 14px;
        font-family: 'Inter', system-ui, sans-serif; color: #ece7da;
        opacity: 0; transition: opacity 600ms ease; }
      #hyperspace-hud.is-on { opacity: 1; }
      #hyperspace-hud .hs-label { font-size: 12px; font-weight: 500; color: #7fb6e8; margin-bottom: 4px; }
      #hyperspace-hud .hs-dest { font-size: 17px; font-weight: 500; color: #ece7da; font-variant-numeric: tabular-nums; }
      #hyperspace-hud .hs-bar { height: 4px; margin: 12px auto 8px; width: 240px; border-radius: 2px;
        background: #232a36; overflow: hidden; }
      #hyperspace-hud .hs-fill { height: 100%; width: 0; background: #7fb6e8; }
      #hyperspace-hud .hs-eta { font-size: 12px; color: #a8a396; font-variant-numeric: tabular-nums; }`;
    document.head.appendChild(style);
    document.body.appendChild(el);
    hud = { el, dest: el.querySelector('.hs-dest'), fill: el.querySelector('.hs-fill'), eta: el.querySelector('.hs-eta') };
    return hud;
  }

  // ---- timeline -----------------------------------------------------------
  let run = null;
  const fx = { fovOffset: 0, shake: 0, active: false };
  const shakeQ = new THREE.Quaternion();
  const shakeE = new THREE.Euler();

  function start({
    cruiseSeconds = 6,
    destinationName = '',
    distanceLy = 0,
    fromColor = null,
    toColor = null,
    seed = Math.random() * 1000,
    onSwap = null,
    onComplete = null
  } = {}) {
    const total = SPOOL + PUNCH + cruiseSeconds + EXIT;
    // A handful of drive surges scattered through the cruise.
    const surges = [];
    const nSurge = Math.max(1, Math.round(cruiseSeconds / 3.2));
    let s = seed;
    const rnd = () => { s = (s * 16807 + 0.5) % 2147483647; return (s % 10000) / 10000; };
    for (let i = 0; i < nSurge; i++) {
      surges.push({ at: SPOOL + PUNCH + (0.1 + 0.8 * rnd()) * cruiseSeconds, power: 0.35 + rnd() * 0.5 });
    }
    run = {
      t: 0, total, cruise: cruiseSeconds, swapAt: SPOOL + PUNCH + cruiseSeconds * 0.45,
      swapped: false, travel: 0, surges, onSwap, onComplete, seed,
      distanceLy, destinationName
    };
    if (fromColor) tunnelUniforms.uTintFrom.value.copy(fromColor);
    if (toColor) tunnelUniforms.uTintTo.value.copy(toColor);
    group.visible = true;
    fx.active = true;
    const h = ensureHud();
    h.dest.textContent = destinationName ? `${destinationName}${distanceLy ? ` · ${distanceLy.toFixed(1)} ly` : ''}` : '';
    h.fill.style.width = '0%';
    h.el.classList.add('is-on');
    return total;
  }

  function finish() {
    const done = run && run.onComplete;
    run = null;
    group.visible = false;
    fx.active = false;
    fx.fovOffset = 0;
    fx.shake = 0;
    if (hud) hud.el.classList.remove('is-on');
    if (done) done();
  }

  // Place the group on the camera and advance the timeline.
  // unit: world size of one group unit, chosen by the caller so the
  // tunnel sits in front of everything but the player's ship.
  function update(camera, dt, { unit = 1, occludeWithDepth = false, width = 1, height = 1 } = {}) {
    if (!run) return;
    dt = Math.min(dt, 0.1);
    run.t += dt;
    const t = run.t;
    const T1 = SPOOL, T2 = SPOOL + PUNCH, T3 = SPOOL + PUNCH + run.cruise;

    let speed, aperture, cover, flash = 0, core, rim, fov, shake, dots, tintMix, len;
    if (t < T1) {
      const k = t / SPOOL;
      speed = 0.18 * easeInCubic(k);
      len = 0.4 + 26 * easeInCubic(k);
      aperture = 2.3;
      cover = smooth(0.55, 1, k);
      core = 0.4 * k;
      rim = 0;
      fov = -5 * easeInOut(k);
      shake = 0.35 * easeInCubic(k);
      dots = smooth(0, 0.3, k);
      tintMix = 0;
    } else if (t < T2) {
      const k = (t - T1) / PUNCH;
      const ek = easeInCubic(k);
      speed = lerp(0.18, 1, ek);
      len = lerp(26, 70, ek);
      aperture = lerp(1.25, -0.5, easeOutCubic(k));
      cover = 1;
      core = lerp(0.4, 1, k);
      rim = Math.sin(k * Math.PI) * 1.4;
      flash = Math.exp(-Math.pow((k - 0.55) * 4.2, 2)) * 1.1;
      fov = lerp(-5, 34, easeOutCubic(k));
      shake = 0.35 + Math.sin(k * Math.PI) * 1.2;
      dots = 1;
      tintMix = 0;
    } else if (t < T3) {
      const k = (t - T2) / run.cruise;
      let surge = 0;
      for (const s of run.surges) surge += Math.exp(-Math.pow((t - s.at) / 0.35, 2)) * s.power;
      const breath = Math.sin(t * 1.3 + run.seed) * 0.06 + Math.sin(t * 3.1) * 0.03;
      speed = 1 + breath + surge * 0.6;
      len = 70 * (1 + surge * 0.45);
      aperture = -0.5;
      cover = 1;
      core = 1 + surge * 0.8;
      rim = 0;
      fov = 34 + breath * 20 + surge * 7;
      shake = 0.28 + surge * 1.1 + Math.abs(breath) * 0.8;
      dots = 1;
      tintMix = smooth(0.3, 0.95, k);
    } else if (t < run.total) {
      const k = (t - T3) / EXIT;
      const ek = easeOutCubic(k);
      speed = lerp(1, 0.02, ek);
      len = lerp(70, 0.4, easeOutCubic(Math.min(1, k * 1.25)));
      aperture = lerp(-0.5, 2.4, easeInOut(Math.min(1, k * 1.15)));
      cover = 1 - smooth(0.7, 1, k);
      core = 1 - k;
      rim = Math.sin(Math.min(1, k * 1.4) * Math.PI) * 1.2;
      flash = Math.exp(-Math.pow((k - 0.06) * 7, 2)) * 0.7;
      fov = lerp(34, 0, easeInOut(k));
      shake = lerp(0.9, 0, easeOutCubic(k));
      dots = 1 - smooth(0.55, 1, k);
      tintMix = 1;
    } else {
      finish();
      return;
    }

    if (!run.swapped && t >= run.swapAt) {
      run.swapped = true;
      if (run.onSwap) run.onSwap();
    }

    // Travel: at speed 1 a streak crosses the corridor in ~0.55 s.
    run.travel += speed * dt * CORRIDOR * 1.8;

    group.position.copy(camera.position);
    group.quaternion.copy(camera.quaternion);
    group.scale.setScalar(unit);
    group.updateMatrixWorld(true);

    streakUniforms.uTravel.value = run.travel;
    streakUniforms.uLen.value = len;
    streakUniforms.uNear.value = Math.max((camera.near / unit) * 1.5, 0.02);
    streakUniforms.uResolution.value.set(width, height);
    streakUniforms.uOpacity.value = dots;
    streakUniforms.uWidth.value = Math.max(0.8, height / 900);
    streakUniforms.uTintMix.value = Math.min(1, speed) * 0.55;
    streakMat.depthTest = occludeWithDepth;

    tunnelUniforms.uTime.value += dt;
    tunnelUniforms.uTravel.value = run.travel;
    tunnelUniforms.uAperture.value = aperture;
    tunnelUniforms.uCover.value = cover;
    tunnelUniforms.uFlash.value = flash;
    tunnelUniforms.uCore.value = core;
    tunnelUniforms.uRim.value = rim;
    tunnelUniforms.uSpeed.value = Math.min(1.5, speed);
    tunnelUniforms.uTintMix.value = tintMix;
    // Flash colour: the star's tint pushed into a saturated drive blue,
    // so a white star still flashes as light, not as a white screen.
    tunnelUniforms.uFlashColor.value.copy(t < T3 ? tunnelUniforms.uTintFrom.value : tunnelUniforms.uTintTo.value)
      .lerp(FLASH_BLUE, 0.6);
    tunnelMat.depthTest = occludeWithDepth;

    fx.fovOffset = fov;
    fx.shake = shake;

    if (hud) {
      hud.fill.style.width = `${Math.min(100, (t / run.total) * 100).toFixed(1)}%`;
      const left = Math.max(0, run.total - t);
      hud.eta.textContent = t < T1 ? 'Spooling drive' : t < T3 ? `Arrival ${left.toFixed(1)} s` : 'Dropping out';
    }
  }

  // Apply FOV punch and shake to the camera for this frame's render;
  // returns a function that restores it afterwards.
  function applyCameraFx(camera, time) {
    if (!fx.active) return null;
    const savedFov = camera.fov;
    const savedQ = camera.quaternion.clone();
    camera.fov = THREE.MathUtils.clamp(savedFov + fx.fovOffset, 10, 120);
    camera.updateProjectionMatrix();
    if (fx.shake > 0) {
      const a = fx.shake * 0.0045;
      shakeE.set(
        (Math.sin(time * 37.1) + Math.sin(time * 61.7) * 0.5) * a,
        (Math.sin(time * 41.3 + 1.7) + Math.sin(time * 71.9) * 0.5) * a,
        Math.sin(time * 23.3 + 0.4) * a * 0.6
      );
      shakeQ.setFromEuler(shakeE);
      camera.quaternion.multiply(shakeQ);
      camera.updateMatrixWorld(true);
    }
    return () => {
      camera.fov = savedFov;
      camera.updateProjectionMatrix();
      camera.quaternion.copy(savedQ);
      camera.updateMatrixWorld(true);
    };
  }

  return {
    group,
    start,
    update,
    applyCameraFx,
    isActive: () => !!run,
    progress: () => (run ? run.t / run.total : 0),
    dispose() {
      quad.dispose(); streakMat.dispose();
      tunnel.geometry.dispose(); tunnelMat.dispose();
      if (group.parent) group.parent.remove(group);
      if (hud) hud.el.remove();
    }
  };
}

export const HYPERSPACE_TIMING = { spool: SPOOL, punch: PUNCH, exit: EXIT };
export { kelvinToRgb };
